import {
  createListItem,
  deleteListItem,
  getListItems,
  updateListItem,
} from '../graph/lists';
import { LISTS } from './listNames';
import { toUserRole, type UserRoleFields } from './mappers';
import { resolveAccess, serializeDelimited } from './scope';
import type { AppUser, UserRole } from './types';

function toFields(entry: Omit<UserRole, 'id'>): UserRoleFields {
  return {
    Title: entry.displayName,
    PrincipalType: entry.principalType,
    UPN: entry.principalType === 'user' ? entry.upn : '',
    GroupId: entry.principalType === 'group' ? entry.groupId : '',
    Role: entry.role,
    Districts: serializeDelimited(entry.districts),
    StoreNumbers: serializeDelimited(entry.storeNumbers),
    Active: entry.active,
  };
}

/**
 * Resolve the signed-in identity to an app role. `groupIds` comes from the
 * ID token's groups claim (docs/entra-setup.md §7), enabling role assignment
 * to Entra security groups. Returns null when nothing matches — the app
 * shows "not authorized".
 *
 * The whole list is fetched and matched client-side: it is small (tens of
 * rows), and matching in code supports the user-OR-any-group condition
 * without compound indexed filters.
 */
export async function resolveAppUser(
  upn: string,
  fallbackDisplayName: string,
  groupIds: readonly string[],
): Promise<AppUser | null> {
  const rows = await getAllUserRoles();
  const access = resolveAccess(rows, upn, groupIds);
  if (!access) return null;
  return {
    upn,
    displayName: access.displayName || fallbackDisplayName,
    role: access.role,
    districts: access.districts,
    storeNumbers: access.storeNumbers,
  };
}

export async function getAllUserRoles(): Promise<UserRole[]> {
  // No $orderby: SharePoint requires indexed columns for orderby as well as
  // filter, and Title isn't indexed. The list is small — sort client-side.
  const items = await getListItems<UserRoleFields>(LISTS.userRoles);
  return items
    .map((i) => toUserRole(i.id, i.fields))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

export async function createUserRole(
  entry: Omit<UserRole, 'id'>,
): Promise<UserRole> {
  const created = await createListItem<UserRoleFields>(
    LISTS.userRoles,
    toFields(entry),
  );
  return toUserRole(created.id, created.fields);
}

export async function updateUserRole(entry: UserRole): Promise<void> {
  await updateListItem<UserRoleFields>(
    LISTS.userRoles,
    entry.id,
    toFields(entry),
  );
}

export async function deleteUserRole(id: string): Promise<void> {
  await deleteListItem(LISTS.userRoles, id);
}
